import { useStore } from "@/store";
import { Permission } from "./config";
import { useCallback, useMemo } from "react";
import { isEducationManagerSession } from "@/core/auth/utils/education-manager-session";
import {
  EDUCATION_MANAGER_FORBIDDEN_KEYS,
  EDUCATION_MANAGER_IMPLICIT_KEYS,
} from "./education-manager-restrictions";

// Permission kontrolü için hook
export const usePermission = () => {
  const { isStaff, permissionsArray, userType, currentUser } = useStore();
  // Eğitim Yöneticisi oturumunda bazı yetkiler backend verse bile UI'da
  // kapatılır (USERS.CREATE/DELETE/UPDATE_PASSWORD, AUDIT_LOGS.*); USERS.VIEW_ALL
  // ise backend vermese bile UI'da açılır — havuza personel ekleyebilmesi için.
  const isEduMgr = isEducationManagerSession(userType, currentUser);

  const checkKey = useCallback(
    (key: string): boolean => {
      if (isEduMgr && EDUCATION_MANAGER_FORBIDDEN_KEYS.has(key)) return false;
      if (isEduMgr && EDUCATION_MANAGER_IMPLICIT_KEYS.has(key)) return true;
      return permissionsArray.includes(key);
    },
    [isEduMgr, permissionsArray],
  );

  const hasPermission = useCallback((permission: Permission): boolean => {
    const requiredPermissions =
      isStaff && permission.staff ? permission.staff : permission.admin;
    if (!requiredPermissions) return false;
    const permissions = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];
    return permissions.every(checkKey);
  }, [isStaff, checkKey]);

  /** API'den gelen key ile direkt kontrol (örn. "ANNOUNCEMENTS.CREATE") */
  const hasPermissionKey = useCallback(
    (key: string): boolean => checkKey(key),
    [checkKey],
  );

  return { hasPermission, hasPermissionKey };
};

/**
 * Per-education yönetici kontrolü: oturumdaki kullanıcı, verilen eğitimin
 * `managers` listesinde mi? EDUCATION_SUPERVISOR rolündeki bir kullanıcı bir
 * eğitime "Eğitim Yöneticisi" olarak atandığında, sadece o eğitim üzerinde
 * EDUCATION_MANAGER yetkilerine sahip olur — bu kontrol UI tarafında
 * butonların doğru gösterilmesi için kullanılır.
 *
 * Backend `EducationAccessGuard` zaten aynı kontrolü server-side yapar; bu hook
 * yalnızca görsel feedback amaçlıdır. Yeni API çağrısı YOK; mevcut education
 * detail response'undaki `managers` array'inden okur.
 */
export const useIsEducationManagerOf = (
  education: { managers?: Array<{ userID?: string | null }> | null } | null | undefined,
): boolean => {
  const currentUserId = useStore((s) => s.currentUser?.id);
  return useMemo(() => {
    if (!currentUserId) return false;
    const managers = education?.managers;
    if (!managers?.length) return false;
    return managers.some((m) => m?.userID === currentUserId);
  }, [currentUserId, education?.managers]);
};
